import { pack } from '@attain-apps/data-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, standardNodeCreator } from './utils'

/**
 * TODO (MAX(_) or GREATEST(_, _, _))
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface Max<out T = any> {
  /** {@link Max} */
  readonly [Key.Max]: Expression<readonly any[]> | readonly Expression<any>[]
}
export namespace Max {
  export const isMax: TypeChecker<Max> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Max>({
      [Key.Max]: CommonArgs.isExpressionOrListOfExpressions as TypeChecker<
        Max[Key.Max]
      >,
    }),
  ).usingAlias('Fn.Max')
  export const assertIsMax: TypeAsserter<Max> = TypeAsserter.Factory.from(isMax)

  /**
   * @see {@link Node}
   */
  export class Node<T = any> extends fnNode.Node<
    T,
    Key.Max,
    readonly expressionNode.Node[]
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type | undefined {
      return common.value.types.Intrinsic.Generalized.from(
        ...this.arg.map(node => node.type).filter(TypeChecker.isNotNullish.raw),
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.Max, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<Max>['expressionsInArgs'] = fn =>
    pack(fn[Key.Max])
  export const HELPER: FnHelper<Max> = {
    createNode: standardNodeCreator.usingExpressionList({
      // What should the max of no items be? I guess undefined
      emptyValue: undefined,
      finalizeArgs: (args, stash) => [
        ...args,
        // If we have a defined max, we include that
        ...(stash.max === undefined
          ? []
          : [new expressionNode.ForValue(stash.max)]),
      ],
      getArgExpressions: expressionsInArgs,
      handleValue: (node, stash) => {
        // NOTE: we cannot use Math.max since we want to support more than just
        // numbers
        for (const val of pack(node.value)) {
          // If we encounter a value that is more than the current max, we
          // update the max
          if (stash.max === undefined || (val as any) > stash.max) {
            stash.max = val
          }
        }
      },
      nodeClass: Node,
      setupStash: (): { max?: any } => ({}),
    }),
    expressionsInArgs,
    typeChecker: isMax,
  }
}
